/**
 * Deep chart data for the Kundli Studio — divisional chart SVGs, full planet
 * tables (AllPlanetData) and house/bhava tables (AllHouseData).
 *
 * These use VedAstro's GET URL calls (`/AllPlanetData/PlanetName/All/...`),
 * which the POST body protocol doesn't support ("All" fails to parse there).
 * Every fetch is memoised per input for the session so switching tabs or
 * chart styles never re-spends a paid API call on identical parameters.
 */

import { BASE, AYANAMSA, authHeaders, VedAstroError } from './vedastro.js'

/* ─────────────────────────── Session memo ─────────────────────────── */

// Stores in-flight promises so concurrent identical requests share one call.
// A failed call is evicted so the user can retry. Shared by every studio
// module (dashas, ashtakavarga, doshas) so no paid call ever repeats.
const memo = new Map()

export function memoized(key, make) {
  if (!memo.has(key)) {
    const p = make().catch((err) => {
      memo.delete(key)
      throw err
    })
    memo.set(key, p)
  }
  return memo.get(key)
}

/* ─────────────────────────── URL helpers ─────────────────────────── */

/** "HH:MM/dd/mm/yyyy/+05:30" path segments, URL-encoded (the + must escape). */
function urlTime(dob, tob, tz = '+05:30') {
  const [y, m, d] = String(dob).split('-')
  const hm = /^\d{1,2}:\d{2}/.test(tob || '') ? tob.slice(0, 5) : '12:00'
  return `${encodeURIComponent(hm)}/${d}/${m}/${y}/${encodeURIComponent(tz)}`
}

/**
 * "lat,lon" location path segment — VedAstro uses raw coordinates verbatim
 * instead of re-geocoding the place name server-side. Names are ambiguous
 * (a search for "Khatauli" returns nine towns), so the server's pick can
 * differ from the suggestion the user chose; and coordinates decide
 * knife-edge charts — an Ascendant within arcseconds of a varga boundary
 * flips a divisional chart's whole house ring. Falls back to the name for
 * a location without coordinates. (JSON-in-path is NOT supported: the API
 * silently swaps it for a default location rather than failing.)
 */
function urlLocation(location) {
  const lat = Number(location?.Latitude)
  const lon = Number(location?.Longitude)
  if (Number.isFinite(lat) && Number.isFinite(lon)) return encodeURIComponent(`${lat},${lon}`)
  return encodeURIComponent(location?.Name || '')
}

async function getRaw(path, signal) {
  let res
  try {
    res = await fetch(`${BASE}/${path}`, { headers: authHeaders(), signal })
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    throw new VedAstroError('Could not reach the astrology service. Check your connection and try again.')
  }
  if (res.status === 429) {
    throw new VedAstroError('The astrology service is busy right now. Please wait a moment and try again.', {
      rateLimited: true,
    })
  }
  if (!res.ok) throw new VedAstroError(`The astrology service returned an error (${res.status}). Please try again.`)
  return res
}

/** GET a JSON calculator and unwrap `{ Status: 'Pass', Payload: { Method: … } }`. */
async function getJson(path, method, signal) {
  const res = await getRaw(path, signal)
  let json
  try {
    json = await res.json()
  } catch {
    throw new VedAstroError('Received an unexpected response from the astrology service.')
  }
  if (json?.Status !== 'Pass') {
    const msg = typeof json?.Payload === 'string' ? json.Payload : 'The astrology service could not complete the calculation.'
    throw new VedAstroError(msg)
  }
  return json.Payload?.[method] ?? json.Payload
}

const toBool = (v) => v === true || v === 'True' || v === 'true'
const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)

/** `{ Name, DegreesIn }` → `{ sign, deg, dms }` (or null-ish placeholders). */
function signDeg(v) {
  if (!v || typeof v !== 'object') return { sign: v || null, deg: 0, dms: '' }
  return {
    sign: v.Name || null,
    deg: toNum(v.DegreesIn?.TotalDegrees),
    dms: String(v.DegreesIn?.DegreeMinuteSecond || '').replace(/\s+/g, ' ').trim(),
  }
}

/* ─────────────────────────── Divisional charts ─────────────────────────── */

export const CHART_STYLES = [
  { key: 'South', label: 'South Indian' },
  { key: 'North', label: 'North Indian' },
]

/**
 * Every varga VedAstro's chart renderer supports, with its classical life-area.
 * `key` is the exact ChartType enum value the API accepts (verified live).
 */
export const CHART_TYPES = [
  { key: 'RasiD1', short: 'D1', name: 'Rasi · Lagna Chart', about: 'The master birth chart — body, self and the whole life at a glance.' },
  { key: 'HoraD2', short: 'D2', name: 'Hora', about: 'Wealth, prosperity and the flow of resources.' },
  { key: 'DrekkanaD3', short: 'D3', name: 'Drekkana', about: 'Siblings, courage and personal initiative.' },
  { key: 'ChaturthamshaD4', short: 'D4', name: 'Chaturthamsha', about: 'Fortune, property, home and fixed assets.' },
  { key: 'SaptamshaD7', short: 'D7', name: 'Saptamsha', about: 'Children, progeny and creative legacy.' },
  { key: 'NavamshaD9', short: 'D9', name: 'Navamsha', about: 'Marriage, dharma and the strength of planets — the chart of destiny.' },
  { key: 'DashamamshaD10', short: 'D10', name: 'Dashamamsha', about: 'Career, profession and public standing.' },
  { key: 'DwadashamshaD12', short: 'D12', name: 'Dwadashamsha', about: 'Parents, lineage and ancestral karma.' },
  { key: 'ShodashamshaD16', short: 'D16', name: 'Shodashamsha', about: 'Vehicles, comforts and material happiness.' },
  { key: 'VimshamshaD20', short: 'D20', name: 'Vimshamsha', about: 'Spiritual practice, worship and inner devotion.' },
  { key: 'ChaturvimshamshaD24', short: 'D24', name: 'Chaturvimshamsha', about: 'Education, learning and knowledge.' },
  { key: 'BhamshaD27', short: 'D27', name: 'Bhamsha', about: 'Innate strengths and weaknesses.' },
  { key: 'TrimshamshaD30', short: 'D30', name: 'Trimshamsha', about: 'Misfortunes, health challenges and adversity.' },
  { key: 'KhavedamshaD40', short: 'D40', name: 'Khavedamsha', about: 'Auspicious and inauspicious life events.' },
  { key: 'AkshavedamshaD45', short: 'D45', name: 'Akshavedamsha', about: 'Character, conduct and moral nature.' },
  { key: 'ShashtyamshaD60', short: 'D60', name: 'Shashtyamsha', about: 'Past-life karma — the subtlest of all vargas.' },
]

/**
 * Fetch a ready-drawn chart as inline SVG markup from the API's own renderer.
 * `style` is 'South' | 'North'; `chartType` a CHART_TYPES key.
 * Script tags are stripped defensively before the markup is trusted inline.
 */
export async function fetchChartSvg({ dob, tob, location, chartType = 'RasiD1', style = 'South' }, signal) {
  const key = `svg|${style}|${chartType}|${dob}|${tob}|${urlLocation(location)}`
  return memoized(key, async () => {
    const path =
      `${style}IndianChart/Location/${urlLocation(location)}` +
      `/Time/${urlTime(dob, tob)}/ChartType/${chartType}/Ayanamsa/${AYANAMSA}`
    const res = await getRaw(path, signal)
    const text = await res.text()
    // A failed calculation replies JSON instead of SVG — surface its message.
    if (text.trimStart().startsWith('{')) {
      let msg = 'The chart could not be drawn for these details.'
      try {
        const j = JSON.parse(text)
        if (typeof j?.Payload === 'string') msg = j.Payload
      } catch { /* keep default message */ }
      throw new VedAstroError(msg)
    }
    if (!text.includes('<svg')) throw new VedAstroError('Received an unexpected response from the astrology service.')
    return text.replace(/<script[\s\S]*?<\/script>/gi, '')
  })
}

/* ─────────────────────────── All planet data ─────────────────────────── */

const DIGNITIES = [
  ['IsPlanetExalted', 'Exalted'],
  ['IsPlanetInMoolatrikona', 'Moolatrikona'],
  ['IsPlanetInOwnSign', 'Own Sign'],
  ['IsPlanetDebilitated', 'Debilitated'],
  ['IsPlanetInFriendSign', 'Friend Sign'],
  ['IsPlanetInEnemySign', 'Enemy Sign'],
]

// PlanetXxxD9Sign-style field for each varga, for the divisional-positions view.
const VARGA_FIELDS = {
  D1: 'PlanetRasiD1Sign', D2: 'PlanetHoraD2Signs', D3: 'PlanetDrekkanaD3Sign', D4: 'PlanetChaturthamshaD4Sign',
  D7: 'PlanetSaptamshaD7Sign', D9: 'PlanetNavamshaD9Sign', D10: 'PlanetDashamamshaD10Sign', D12: 'PlanetDwadashamshaD12Sign',
  D16: 'PlanetShodashamshaD16Sign', D20: 'PlanetVimshamshaD20Sign', D24: 'PlanetChaturvimshamshaD24Sign', D27: 'PlanetBhamshaD27Sign',
  D30: 'PlanetTrimshamshaD30Sign', D40: 'PlanetKhavedamshaD40Sign', D45: 'PlanetAkshavedamshaD45Sign', D60: 'PlanetShashtyamshaD60Sign',
}

function normalizePlanet(name, d) {
  const rasi = signDeg(d.PlanetRasiD1Sign)
  const [nakName, padaRaw] = String(d.PlanetConstellation || '').split(' - ')
  const dignity = (DIGNITIES.find(([flag]) => toBool(d[flag])) || [null, 'Neutral'])[1]
  const vargas = {}
  for (const [short, field] of Object.entries(VARGA_FIELDS)) vargas[short] = signDeg(d[field])
  return {
    name,
    sign: rasi.sign,
    deg: rasi.deg,
    dms: rasi.dms,
    signLord: d.PlanetLordOfZodiacSign?.Name || '—',
    nakshatra: (nakName || '').trim(),
    pada: (padaRaw || '').trim(),
    nakshatraLord: d.PlanetLordOfConstellation?.Name || '—',
    house: Number(String(d.HousePlanetOccupiesBasedOnSign || '').replace('House', '')) || null,
    navamsaSign: signDeg(d.PlanetNavamshaD9Sign).sign,
    longitude: String(d.PlanetNirayanaLongitude?.DegreeMinuteSecond || '').replace(/\s+/g, ' ').trim(),
    retrograde: toBool(d.IsPlanetRetrograde),
    combust: toBool(d.IsPlanetCombust),
    motion: d.PlanetMotionName || '—',
    dignity,
    vargottama: toBool(d.IsPlanetVargottama),
    benefic: toBool(d.IsPlanetBenefic),
    strongInShadbala: toBool(d.IsPlanetStrongInShadbala),
    shadbala: toNum(d.PlanetShadbalaPinda),
    speed: toNum(d.PlanetSpeed),
    vargas,
    // Absolute sidereal longitude 0–360° — used for Kalsarpa axis checks.
    absDeg: toNum(d.PlanetNirayanaLongitude?.TotalDegrees),
    // Benefic/malefic tendency scores; `afflicted` is the classical remedy
    // trigger — weak in Shadbala or Kashta (malefic) outweighing Ishta.
    ishta: toNum(d.PlanetIshtaScore),
    kashta: toNum(d.PlanetKashtaScore),
    afflicted: !toBool(d.IsPlanetStrongInShadbala) || toNum(d.PlanetKashtaScore) > toNum(d.PlanetIshtaScore),
    // Shadbala components in Virupas (Chesta is undefined for Sun/Moon).
    balas: {
      sthana: toNum(d.PlanetSthanaBala),
      dig: toNum(d.PlanetDigBala),
      kala: toNum(d.PlanetKalaBala),
      chesta: d.PlanetChestaBala != null ? toNum(d.PlanetChestaBala) : null,
      naisargika: toNum(d.PlanetNaisargikaBala),
      drik: toNum(d.PlanetDrikBala),
    },
    powerPct: toNum(d.PlanetPowerPercentage),
  }
}

/**
 * One paid call → the complete data set for all 9 grahas (position, lords,
 * nakshatra, dignity, retro/combust, vargas, strength). Returns rows in the
 * classical order Sun…Ketu.
 */
export async function getAllPlanetData({ dob, tob, location }, signal) {
  const key = `planets|${dob}|${tob}|${urlLocation(location)}`
  return memoized(key, async () => {
    const path = `AllPlanetData/PlanetName/All/Location/${urlLocation(location)}/Time/${urlTime(dob, tob)}/Ayanamsa/${AYANAMSA}`
    const list = await getJson(path, 'AllPlanetData', signal)
    if (!Array.isArray(list)) throw new VedAstroError('Received an unexpected response from the astrology service.')
    return list.map((entry) => {
      const name = Object.keys(entry)[0]
      return normalizePlanet(name, entry[name] || {})
    })
  })
}

/* ─────────────────────────── All house data ─────────────────────────── */

// Classical bhava names, 1st → 12th.
export const HOUSE_NAMES = [
  'Tanu (Self)', 'Dhana (Wealth)', 'Sahaja (Siblings)', 'Sukha (Home)',
  'Putra (Children)', 'Ari (Obstacles)', 'Kalatra (Marriage)', 'Randhra (Longevity)',
  'Dharma (Fortune)', 'Karma (Career)', 'Labha (Gains)', 'Vyaya (Loss & Moksha)',
]

/** "Begin:249.65, Middle:261.89, End:279.65" → { begin, middle, end } numbers. */
function parseHouseLongitude(str) {
  const out = { begin: null, middle: null, end: null }
  for (const part of String(str || '').split(',')) {
    const [k, v] = part.split(':')
    const kk = (k || '').trim().toLowerCase()
    if (kk in out) out[kk] = toNum(v)
  }
  return out
}

/**
 * House<Varga>Sign field per varga — the engine's OWN divisional cusp sign.
 * For house 1 this is the varga's Ascendant, which is what a North Indian
 * divisional chart's house ring is built from. Note D1's field breaks the
 * naming pattern: it's `HouseRasiSign`, not `HouseRasiD1Sign`.
 */
const HOUSE_VARGA_FIELDS = {
  D1: 'HouseRasiSign', D2: 'HouseHoraD2Sign', D3: 'HouseDrekkanaD3Sign', D4: 'HouseChaturthamshaD4Sign',
  D7: 'HouseSaptamshaD7Sign', D9: 'HouseNavamshaD9Sign', D10: 'HouseDashamamshaD10Sign', D12: 'HouseDwadashamshaD12Sign',
  D16: 'HouseShodashamshaD16Sign', D20: 'HouseVimshamshaD20Sign', D24: 'HouseChaturvimshamshaD24Sign', D27: 'HouseBhamshaD27Sign',
  D30: 'HouseTrimshamshaD30Sign', D40: 'HouseKhavedamshaD40Sign', D45: 'HouseAkshavedamshaD45Sign', D60: 'HouseShashtyamshaD60Sign',
}

function normalizeHouse(key, d) {
  const num = Number(String(key).replace('House', '')) || 0
  const cusp = signDeg(d.HouseRasiSign)
  const [nakName, padaRaw] = String(d.HouseConstellation || '').split(' - ')
  const vargas = {}
  for (const [short, field] of Object.entries(HOUSE_VARGA_FIELDS)) vargas[short] = signDeg(d[field])
  return {
    vargas,
    house: num,
    label: HOUSE_NAMES[num - 1] || `House ${num}`,
    sign: cusp.sign,
    deg: cusp.deg,
    dms: cusp.dms,
    ...parseHouseLongitude(d.HouseLongitude),
    lord: d.LordOfHouse?.Name || '—',
    nakshatra: (nakName || '').trim(),
    pada: (padaRaw || '').trim(),
    nakshatraLord: d.HouseConstellationLord?.Name || '—',
    planets: Array.isArray(d.PlanetsInHouseBasedOnSign) ? d.PlanetsInHouseBasedOnSign : [],
    aspectedBy: Array.isArray(d.PlanetsAspectingHouse) ? d.PlanetsAspectingHouse : [],
    strength: toNum(d.HouseStrength),
    strengthCategory: d.HouseStrengthCategory || '—',
  }
}

/**
 * One paid call → all 12 bhavas: cusp sign + exact start/middle/end degrees,
 * house lord, cusp nakshatra, occupants, aspecting planets and Bhava Bala.
 */
export async function getAllHouseData({ dob, tob, location }, signal) {
  const key = `houses|${dob}|${tob}|${urlLocation(location)}`
  return memoized(key, async () => {
    const path = `AllHouseData/HouseName/All/Location/${urlLocation(location)}/Time/${urlTime(dob, tob)}/Ayanamsa/${AYANAMSA}`
    const list = await getJson(path, 'AllHouseData', signal)
    if (!Array.isArray(list)) throw new VedAstroError('Received an unexpected response from the astrology service.')
    return list
      .map((entry) => {
        const key = Object.keys(entry)[0]
        return normalizeHouse(key, entry[key] || {})
      })
      .sort((a, b) => a.house - b.house)
  })
}
